import React, { useState, useEffect, useMemo, useContext } from 'react';
import { observer } from 'mobx-react-lite';
import { Context } from '../../../index';
import Sidebar from './sidebar';
import MyOrdersCancelTheApplication from './modal_windows/MyOrdersCancelTheApplication';
import MyOrdersProductPositions from './modal_windows/MyOrdersProductPositions';

import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';

import {
  StatCard, RefreshButton, FilterPill, SearchField,
  StatusBadge, TableCard, LoadingState, EmptyState,
  Pagination, BulkBar,
} from '../../ui/CrmPage';

const KZT = new Intl.NumberFormat('ru-KZ', { maximumFractionDigits: 0 });

const formatDate = (dateString) => {
  if (!dateString) return '—';
  const d = new Date(dateString);
  return d.toLocaleDateString('ru-RU', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    timeZone: 'Europe/Moscow',
  });
};

const MyOrders = observer(() => {
  const { user } = useContext(Context);
  const activeClientId = user?.activeClient?.id ?? null;

  const [orders, setOrders] = useState([]);
  const [statuses, setStatuses] = useState({});
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showProductModal, setShowProductModal] = useState(false);
  const [selectedOrderDetails, setSelectedOrderDetails] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('Все');
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);
  const ordersPerPage = 15;

  useEffect(() => {
    fetchStatuses();
  }, []);

  useEffect(() => {
    fetchOrders();
    setCurrentPage(1);
    setSelectedOrder(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeClientId, user?.user?.id, reloadKey]);

  const fetchOrders = () => {
    const authUserId = Number(user?.user?.id) || null;
    if (!authUserId) {
      setOrders([]); setLoading(false);
      return;
    }
    setLoading(true);
    fetch(`${process.env.REACT_APP_API_URL}v1/order_view`)
      .then((r) => r.json())
      .then((data) => {
        const filtered = Array.isArray(data)
          ? data.filter((o) => activeClientId
              ? Number(o.id_client) === Number(activeClientId)
              : Number(o.id_user) === authUserId)
          : [];
        const grouped = groupOrders(filtered);
        updateOrdersWithPaymentStatus(grouped);
      })
      .catch((e) => console.error('Ошибка загрузки заказов:', e))
      .finally(() => setLoading(false));
  };

  const fetchStatuses = () => {
    fetch(`${process.env.REACT_APP_API_URL}v1/statuses`)
      .then((r) => r.json())
      .then((data) => {
        if (!Array.isArray(data)) return;
        const map = data.reduce((acc, s) => { acc[s.name] = s.id; return acc; }, {});
        setStatuses(map);
      })
      .catch((e) => console.error('Ошибка загрузки статусов:', e));
  };

  const groupOrders = (list) => {
    const byId = list.reduce((acc, o) => {
      if (!acc[o.id_orders]) acc[o.id_orders] = [];
      acc[o.id_orders].push(o);
      return acc;
    }, {});
    return Object.values(byId);
  };

  const fetchPaymentStatus = async (orderId, statusName) => {
    try {
      const route = statusName === 'Отменен пользователем' ? `v1/tickets/${orderId}` : `v1/orders/${orderId}`;
      const r = await fetch(process.env.REACT_APP_API_URL + route);
      if (!r.ok) throw new Error('Ошибка получения данных');
      const data = await r.json();
      const s = data.id_payment_status;
      if (s === 2) return 'Оплачен';
      if (s === 1) return 'Ожидает оплаты';
      return s === null ? 'null' : 'Неизвестен';
    } catch {
      return 'Неизвестен';
    }
  };

  const updateOrdersWithPaymentStatus = async (groups) => {
    const updated = await Promise.all(
      groups.map(async (group) => {
        const first = group[0];
        const paymentStatus = await fetchPaymentStatus(first.id_orders, first.status_name);
        return group.map((item) => ({ ...item, payment_status: paymentStatus }));
      })
    );
    setOrders(updated);
  };

  const handleCheckboxChange = (order) => {
    if (selectedOrder && selectedOrder[0].id_orders === order[0].id_orders) {
      setSelectedOrder(null);
    } else {
      setSelectedOrder(order);
    }
  };

  const handleCancelOrder = async () => {
    const orderId = selectedOrder[0].id_orders;
    try {
      const getResp = await fetch(`${process.env.REACT_APP_API_URL}v1/orders/${orderId}`);
      if (!getResp.ok) throw new Error('Ошибка при получении данных заказа');
      const orderData = await getResp.json();
      const updatedOrder = { ...orderData, id_status: statuses['Отменен пользователем'] };
      const putResp = await fetch(`${process.env.REACT_APP_API_URL}v1/orders/${orderId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedOrder),
      });
      if (!putResp.ok) {
        const text = await putResp.text();
        throw new Error(`Ошибка при обновлении: ${text}`);
      }
      setOrders((prev) =>
        prev.map((group) =>
          group[0].id_orders === orderId
            ? group.map((o) => ({ ...o, status_name: 'Отменен пользователем' }))
            : group
        )
      );
      setShowCancelModal(false);
      setSelectedOrder(null);
    } catch (e) {
      console.error('Ошибка при обновлении данных заказа:', e);
    }
  };

  const handleRowClick = async (group) => {
    const orderId = group[0].id_orders;
    const resp = await fetch(`${process.env.REACT_APP_API_URL}v1/order_view`);
    if (!resp.ok) return;
    const all = await resp.json();
    const details = all.filter((o) => o.id_orders === orderId);
    setSelectedOrderDetails(details);
    setShowProductModal(true);
  };

  // --- summary
  const summary = useMemo(() => {
    const total = orders.length;
    const pending = orders.filter(g => /ожидает/i.test(g[0].status_name || '')).length;
    const inWork = orders.filter(g => /в пути|производ|собира|на доставке/i.test(g[0].status_name || '')).length;
    const closed = orders.filter(g => /закрыт|доставлен|завершен/i.test(g[0].status_name || '')).length;
    return { total, pending, inWork, closed };
  }, [orders]);

  // --- статусы для filter-pills
  const statusGroups = useMemo(() => {
    const unique = new Set(orders.map(g => g[0].status_name).filter(Boolean));
    return Array.from(unique);
  }, [orders]);

  const filteredOrders = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return orders.filter((group) => {
      const o = group[0];
      if (statusFilter !== 'Все' && o.status_name !== statusFilter) return false;
      if (!term) return true;
      return (
        (o.client_name || '').toLowerCase().includes(term) ||
        (o.delivery_address || '').toLowerCase().includes(term) ||
        String(o.order_number || '').includes(term) ||
        (o.status_name || '').toLowerCase().includes(term)
      );
    });
  }, [orders, searchTerm, statusFilter]);

  const sorted = useMemo(() => {
    return [...filteredOrders].sort((a, b) => new Date(b[0].order_date) - new Date(a[0].order_date));
  }, [filteredOrders]);

  const indexOfLast = currentPage * ordersPerPage;
  const indexOfFirst = indexOfLast - ordersPerPage;
  const currentOrders = sorted.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(sorted.length / ordersPerPage);

  const resetFilters = () => { setSearchTerm(''); setStatusFilter('Все'); setCurrentPage(1); };
  const hasFilters = searchTerm || statusFilter !== 'Все';

  return (
    <div className="my-orders-container crmpage">
      <div className="my-orders-sidebar">
        <Sidebar />
      </div>
      <div className="my-orders-basic-container crmpage__main">
        <header className="crmpage__head">
          <h1 className="crmpage__title">Мои заказы</h1>
        </header>

        <section className="crmpage__stats">
          <StatCard label="Всего"     value={summary.total}   accent="neutral" />
          <StatCard label="Ожидают"   value={summary.pending} accent="warning" />
          <StatCard label="В работе"  value={summary.inWork}  accent="info" />
          <StatCard label="Закрытые"  value={summary.closed}  accent="success" />
          <RefreshButton onClick={() => setReloadKey(k => k + 1)} />
        </section>

        <section className="crmpage__filters">
          <SearchField
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
            placeholder="Поиск: номер, клиент, адрес…"
          />
          <div className="crmpage__pills">
            <FilterPill
              active={statusFilter === 'Все'}
              onClick={() => { setStatusFilter('Все'); setCurrentPage(1); }}
              count={orders.length}
            >Все</FilterPill>
            {statusGroups.map(s => (
              <FilterPill
                key={s}
                active={statusFilter === s}
                onClick={() => { setStatusFilter(s); setCurrentPage(1); }}
                count={orders.filter(g => g[0].status_name === s).length}
              >{s}</FilterPill>
            ))}
          </div>
          {hasFilters && (
            <button type="button" className="crmpage__reset" onClick={resetFilters}>Сбросить</button>
          )}
        </section>

        <TableCard>
          {loading ? (
            <LoadingState text="Загружаем заказы…" />
          ) : currentOrders.length === 0 ? (
            <EmptyState
              title="Заказов не найдено"
              text={hasFilters ? 'Попробуйте сбросить фильтры.' : 'Здесь будут ваши заказы.'}
            />
          ) : (
            <>
              <div className="crmpage__table-wrap">
                <table className="crmpage__table">
                  <colgroup>
                    <col style={{ width: 40 }} />
                    <col style={{ width: 80 }} />
                    <col />
                    <col />
                    <col style={{ width: 160 }} />
                    <col style={{ width: 130 }} />
                    <col style={{ width: 100 }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th className="crmpage__th-check"></th>
                      <th>Номер</th>
                      <th>Клиент</th>
                      <th className="crmpage__hide-880">Адрес доставки</th>
                      <th>Статус</th>
                      <th className="crmpage__hide-1180">Оплата</th>
                      <th className="crmpage__hide-1000">Доставка</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentOrders.map((group) => {
                      const o = group[0];
                      const isSelected = selectedOrder && selectedOrder[0].id_orders === o.id_orders;
                      const isPending = o.status_name === 'Ожидает подтверждения';
                      return (
                        <tr
                          key={o.id_orders}
                          className={isSelected ? 'crmpage__row crmpage__row--selected' : 'crmpage__row'}
                          onClick={() => handleRowClick(group)}
                        >
                          <td className="crmpage__td-check" onClick={(e) => e.stopPropagation()}>
                            {isPending && (
                              <input
                                type="checkbox"
                                className="crmpage__check"
                                checked={!!isSelected}
                                onChange={() => handleCheckboxChange(group)}
                              />
                            )}
                          </td>
                          <td><span className="crmpage__num">№{o.order_number}</span></td>
                          <td>
                            <div className="crmpage__primary">{o.client_name || '—'}</div>
                            <div className="crmpage__sub">{o.region_name}</div>
                          </td>
                          <td className="crmpage__muted crmpage__hide-880">{o.delivery_address || '—'}</td>
                          <td><StatusBadge name={o.status_name} /></td>
                          <td className="crmpage__hide-1180"><StatusBadge name={o.payment_status} /></td>
                          <td className="crmpage__date crmpage__hide-1000">{formatDate(o.delivery_date)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile */}
              <div className="crmpage__cards">
                {currentOrders.map((group) => {
                  const o = group[0];
                  const isSelected = selectedOrder && selectedOrder[0].id_orders === o.id_orders;
                  return (
                    <div
                      key={o.id_orders}
                      className={`crmpage__card ${isSelected ? 'is-selected' : ''}`}
                      onClick={() => handleRowClick(group)}
                    >
                      <div className="crmpage__card-head">
                        <span className="crmpage__num">№{o.order_number}</span>
                        <StatusBadge name={o.status_name} />
                      </div>
                      <div className="crmpage__card-row">
                        <span className="crmpage__card-label">Клиент</span>
                        <span className="crmpage__card-val">{o.client_name || '—'}</span>
                      </div>
                      <div className="crmpage__card-row">
                        <span className="crmpage__card-label">Адрес</span>
                        <span className="crmpage__card-val">{o.delivery_address || '—'}</span>
                      </div>
                      <div className="crmpage__card-row">
                        <span className="crmpage__card-label">Доставка</span>
                        <span className="crmpage__card-val">{formatDate(o.delivery_date)}</span>
                      </div>
                      <div className="crmpage__card-row">
                        <span className="crmpage__card-label">Оплата</span>
                        <span className="crmpage__card-val"><StatusBadge name={o.payment_status} /></span>
                      </div>
                    </div>
                  );
                })}
              </div>

              <Pagination current={currentPage} total={totalPages} onChange={setCurrentPage} />
            </>
          )}
        </TableCard>

        <BulkBar count={selectedOrder ? 1 : 0} onClear={() => setSelectedOrder(null)}>
          <button
            type="button"
            className="crmpage__btn crmpage__btn--danger"
            onClick={() => setShowCancelModal(true)}
          >
            <CancelOutlinedIcon sx={{ fontSize: 18 }} />
            Отменить заявку
          </button>
        </BulkBar>

        {showCancelModal && (
          <MyOrdersCancelTheApplication
            onClose={() => setShowCancelModal(false)}
            onConfirm={handleCancelOrder}
          />
        )}
        {showProductModal && (
          <MyOrdersProductPositions
            orders={selectedOrderDetails}
            onClose={() => setShowProductModal(false)}
            clientName={selectedOrderDetails[0]?.client_name}
          />
        )}
      </div>
    </div>
  );
});

export default MyOrders;
